import {
    EMPTY_BASE_INVENTORY_BY_ANTENNA_FILTERS,
    filterAntennasForInventory,
    filterRowsByNonZeroStock,
    hasActiveBaseInventoryFilters,
    matchesInventorySearch,
    type BaseInventoryByAntennaFilters,
} from '@app/inventory/inventoryByAntennaFilters';
import type {
    AnimationId,
    AnimationLight,
    AntennaId,
    AntennaLight,
    MissionTypeId,
} from '@wwf/sdk';

export type AnimationInventoryFilters = BaseInventoryByAntennaFilters & {
    missionType: MissionTypeId | '';
    hideNoMaterialRequired: boolean;
};

export const EMPTY_ANIMATION_INVENTORY_FILTERS: AnimationInventoryFilters = {
    ...EMPTY_BASE_INVENTORY_BY_ANTENNA_FILTERS,
    missionType: '',
    hideNoMaterialRequired: false,
};

export function hasActiveAnimationInventoryFilters(
    filters: AnimationInventoryFilters
) {
    return (
        hasActiveBaseInventoryFilters(filters) ||
        filters.missionType !== '' ||
        filters.hideNoMaterialRequired
    );
}

export function filterAnimationsForInventory(
    animations: AnimationLight[],
    filters: AnimationInventoryFilters
) {
    return animations.filter(animation => {
        if (
            filters.hideNoMaterialRequired &&
            animation.noSpecificMaterialRequired
        ) {
            return false;
        }

        if (
            filters.missionType &&
            animation.missionType !== filters.missionType
        ) {
            return false;
        }

        return matchesInventorySearch(
            filters.rowSearch,
            animation.name,
            animation.description,
            animation.theme,
            animation.ageTarget
        );
    });
}

export function applyAnimationInventoryFilters({
    animations,
    antennas,
    filters,
    getInventoryCount,
}: {
    animations: AnimationLight[];
    antennas: AntennaLight[];
    filters: AnimationInventoryFilters;
    getInventoryCount: (
        animationId: AnimationId,
        antennaId: AntennaId
    ) => number | null;
}) {
    const filteredAntennas = filterAntennasForInventory(antennas, filters);
    const filteredAnimations = filterRowsByNonZeroStock({
        rows: filterAnimationsForInventory(animations, filters),
        antennas: filteredAntennas,
        nonZeroStockOnly: filters.nonZeroStockOnly,
        getCount: (animation, antenna) => {
            if (animation.noSpecificMaterialRequired) {
                return null;
            }

            return getInventoryCount(animation.id, antenna.id);
        },
    });

    return {
        animations: filteredAnimations,
        antennas: filteredAntennas,
    };
}
